import { useEffect, useState } from "react";
import {
  Settings as SettingsIcon,
  KeyRound,
  Save,
  ShieldCheck,
  Link2,
  Link2Off,
  PlayCircle,
  ExternalLink,
  Loader2,
  Sparkles,
  Stethoscope,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  X as XIcon,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatDateTimeBR } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [gcalStatus, setGcalStatus] = useState(null);
  const [redirectUri, setRedirectUri] = useState("");
  const [form, setForm] = useState({
    google_client_id: "",
    google_client_secret: "",
    google_calendar_email: "",
  });
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  // Diagnóstico do Auto-Recovery (Fase 9.1)
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);
  const [showDiagnosis, setShowDiagnosis] = useState(false);
  const [copiedRedirect, setCopiedRedirect] = useState(false);

  const loadAll = async () => {
    const [s, g, r] = await Promise.all([
      api.get("/settings"),
      api.get("/google-calendar/status"),
      api.get("/google-calendar/redirect-uri").catch(() => ({ data: { redirect_uri: "" } })),
    ]);
    setSettings(s.data);
    setGcalStatus(g.data);
    setRedirectUri(r.data?.redirect_uri || "");
  };

  useEffect(() => {
    loadAll();
    // Listen for OAuth popup completion
    const handler = (event) => {
      if (event?.data?.type !== "google-calendar-oauth") return;
      if (event.data.success) {
        toast.success(
          `Google Calendar conectado${
            event.data.email ? ` (${event.data.email})` : ""
          }.`
        );
      } else {
        toast.error("Falha ao conectar com o Google Calendar.");
      }
      loadAll();
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (form.google_client_id.trim())
        payload.google_client_id = form.google_client_id.trim();
      if (form.google_client_secret.trim())
        payload.google_client_secret = form.google_client_secret.trim();
      if (form.google_calendar_email.trim())
        payload.google_calendar_email = form.google_calendar_email.trim();
      if (Object.keys(payload).length === 0) {
        toast.error("Preencha pelo menos um campo.");
        return;
      }
      await api.put("/settings", payload);
      toast.success("Configurações atualizadas.");
      setForm({
        google_client_id: "",
        google_client_secret: "",
        google_calendar_email: "",
      });
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/google-calendar/auth/start");
      if (!data?.authorization_url) {
        toast.error("URL de autorização indisponível.");
        return;
      }
      // Open in popup so we can receive postMessage on success
      const w = 520;
      const h = 640;
      const left = window.screen.width / 2 - w / 2;
      const top = window.screen.height / 2 - h / 2;
      const popup = window.open(
        data.authorization_url,
        "google-oauth",
        `width=${w},height=${h},left=${left},top=${top}`
      );
      if (!popup) {
        toast.info("Pop-up bloqueado. Abrindo em nova guia…");
        window.open(data.authorization_url, "_blank");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao iniciar autorização.");
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (
      !window.confirm(
        "Tem certeza que deseja desconectar o Google Calendar? Reuniões existentes continuam, mas novas não poderão ser criadas até a reconexão."
      )
    ) {
      return;
    }
    setDisconnecting(true);
    try {
      await api.post("/google-calendar/disconnect");
      toast.success("Google Calendar desconectado.");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao desconectar.");
    } finally {
      setDisconnecting(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/google-calendar/test");
      if (data.ok) {
        toast.success(data.detail || "Conexão OK.");
      } else {
        toast.error(data.detail || "Falha no teste.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao testar conexão.");
    } finally {
      setTesting(false);
    }
  };

  const handleDiagnose = async () => {
    setDiagnosing(true);
    setShowDiagnosis(true);
    try {
      const { data } = await api.get("/google-calendar/diagnose");
      setDiagnosis(data);
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Erro ao executar diagnóstico."
      );
      setShowDiagnosis(false);
    } finally {
      setDiagnosing(false);
    }
  };

  if (!settings) return <Skeleton className="h-96" />;

  const gConnected = gcalStatus?.connected;
  const gConfigured = gcalStatus?.configured;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <SettingsIcon className="w-5 h-5 text-midnight" /> Configurações
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Gerencie a integração com o Google Calendar — origem das reuniões
          (Google Meet) e da agenda institucional do CEAP.
        </p>
      </div>

      {/* Status card */}
      <Card className="card-soft">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-midnight" /> Status da integração
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            className="p-4 border border-slate-200 rounded-md flex items-start gap-4"
            data-testid="status-google"
          >
            <div className="flex-shrink-0 p-3 rounded-full bg-midnight/5">
              <ShieldCheck
                className={
                  gConnected
                    ? "w-6 h-6 text-emerald-600"
                    : gConfigured
                    ? "w-6 h-6 text-amber-500"
                    : "w-6 h-6 text-slate-400"
                }
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-slate-900">
                  Google Calendar + Google Meet
                </span>
                <Badge
                  className={
                    gConnected
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                      : gConfigured
                      ? "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50"
                      : "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-100"
                  }
                  variant="outline"
                >
                  {gConnected
                    ? "Conectado"
                    : gConfigured
                    ? "Pronto para conectar"
                    : "Não configurado"}
                </Badge>
              </div>
              <p className="text-sm text-slate-600 mt-1">
                {gConnected
                  ? `Reuniões serão criadas automaticamente no Google Calendar com link do Meet pela conta ${
                      gcalStatus?.email || ""
                    }.`
                  : gConfigured
                  ? "Credenciais salvas. Clique em \u201CConectar Google Calendar\u201D abaixo para autorizar a conta institucional."
                  : "Preencha o Client ID e Client Secret abaixo e salve para habilitar a conexão."}
              </p>
              {gcalStatus?.connected_at && (
                <div className="text-[11px] text-slate-400 mt-1">
                  Conectado em {formatDateTimeBR(gcalStatus.connected_at)}
                </div>
              )}
            </div>
          </div>

          {settings.updated_at && (
            <div className="text-xs text-slate-500">
              Última atualização das credenciais:{" "}
              {formatDateTimeBR(settings.updated_at)}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action card */}
      <Card className="card-soft" data-testid="card-google-calendar">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Link2 className="w-4 h-4 text-midnight" /> Conexão Google Calendar
            {gConnected && (
              <Badge className="ml-1 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                Conectado
              </Badge>
            )}
            {!gConnected && gConfigured && (
              <Badge
                variant="outline"
                className="ml-1 border-amber-300 text-amber-700"
              >
                Pendente autorização
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-blue-50/60 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-slate-700">
                <strong className="text-slate-900">
                  Como funciona no CEAP:
                </strong>
                <ol className="mt-2 ml-4 list-decimal space-y-1 text-slate-700">
                  <li>
                    Toda reunião criada no CEAP gera automaticamente um evento
                    no Google Calendar com link do <strong>Google Meet</strong>
                    .
                  </li>
                  <li>
                    Durante a reunião no Meet, ative o{" "}
                    <strong>“Take notes for me”</strong> do Gemini para gerar a
                    ata automaticamente no Drive.
                  </li>
                  <li>
                    Após a reunião, cole a ata gerada pelo Gemini direto no
                    detalhe da reunião no CEAP — pronto para publicar ao
                    cliente.
                  </li>
                </ol>
              </div>
            </div>
          </div>

          <p className="text-sm text-slate-600">
            Conta institucional:{" "}
            <code className="px-1 bg-slate-100 rounded">
              {gcalStatus?.email || "monicaagendalegis@gmail.com"}
            </code>
          </p>

          {!gConfigured && (
            <div className="border border-slate-200 rounded-md p-4 bg-slate-50 text-sm text-slate-700">
              <strong>Antes de conectar:</strong> preencha o{" "}
              <em>Client ID</em> e o <em>Client Secret</em> abaixo (gerados em{" "}
              <a
                className="text-midnight underline"
                href="https://console.cloud.google.com"
                target="_blank"
                rel="noreferrer"
              >
                console.cloud.google.com
              </a>{" "}
              → APIs &amp; Services → Credentials → OAuth client ID → Web
              application).
            </div>
          )}

          {/* Redirect URI block — sempre visível, crítico para evitar redirect_uri_mismatch */}
          {redirectUri && (
            <div
              className="border border-amber-300 rounded-md p-4 bg-amber-50/70"
              data-testid="redirect-uri-block"
            >
              <div className="text-sm text-slate-900 font-semibold mb-1">
                Authorized redirect URI (obrigatório no Google Cloud Console)
              </div>
              <p className="text-xs text-slate-700 mb-2">
                Acesse{" "}
                <a
                  className="text-midnight underline"
                  href="https://console.cloud.google.com/apis/credentials"
                  target="_blank"
                  rel="noreferrer"
                >
                  Google Cloud Console → Credentials
                </a>{" "}
                → seu OAuth Client → <em>Authorized redirect URIs</em> e
                adicione <strong>exatamente</strong> esta URL (qualquer
                diferença gera erro <code>redirect_uri_mismatch</code>):
              </p>
              <div className="flex items-center gap-2">
                <code
                  className="block p-2 bg-white border border-slate-200 rounded text-xs break-all flex-1"
                  data-testid="redirect-uri-value"
                >
                  {redirectUri}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(redirectUri);
                      setCopiedRedirect(true);
                      toast.success("Redirect URI copiada.");
                      setTimeout(() => setCopiedRedirect(false), 2000);
                    } catch {
                      toast.error("Não foi possível copiar.");
                    }
                  }}
                  data-testid="copy-redirect-uri"
                >
                  {copiedRedirect ? "Copiado ✓" : "Copiar"}
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 mt-2 italic">
                Dica: cadastre tanto o domínio de Preview quanto o de Produção
                separadamente no Google Cloud Console — eles são URLs
                diferentes.
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {gConnected ? (
              <>
                <Button
                  onClick={handleTest}
                  disabled={testing}
                  variant="outline"
                  className="border-midnight text-midnight hover:bg-midnight/5"
                  data-testid="google-test-button"
                >
                  {testing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <PlayCircle className="w-4 h-4 mr-2" />
                  )}
                  Testar conexão
                </Button>
                <Button
                  onClick={handleDiagnose}
                  disabled={diagnosing}
                  variant="outline"
                  className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                  data-testid="google-diagnose-button"
                >
                  {diagnosing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Stethoscope className="w-4 h-4 mr-2" />
                  )}
                  Diagnosticar auto-recovery
                </Button>
                <Button
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="google-disconnect-button"
                >
                  {disconnecting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Link2Off className="w-4 h-4 mr-2" />
                  )}
                  Desconectar
                </Button>
              </>
            ) : (
              <Button
                onClick={handleConnect}
                disabled={connecting || !gConfigured}
                className="bg-midnight hover:bg-ink text-white"
                data-testid="google-connect-button"
              >
                {connecting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ExternalLink className="w-4 h-4 mr-2" />
                )}
                {gConfigured
                  ? "Conectar Google Calendar"
                  : "Salve as credenciais para conectar"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Credentials card (only Google) */}
      <Card className="card-soft">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-midnight" /> Credenciais OAuth
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-slate-600">
            Necessário apenas para configurar pela primeira vez ou trocar a
            conta Google institucional. Após salvar, use o botão{" "}
            <strong>“Conectar Google Calendar”</strong> acima.
          </p>
          <div className="space-y-3">
            <div>
              <Label>E-mail da agenda Google</Label>
              <Input
                value={form.google_calendar_email}
                onChange={(e) =>
                  setForm({ ...form, google_calendar_email: e.target.value })
                }
                placeholder="monicaagendalegis@gmail.com"
                className="mt-1"
                data-testid="google-email-input"
              />
            </div>
            <div>
              <Label>Google OAuth Client ID</Label>
              <Input
                value={form.google_client_id}
                onChange={(e) =>
                  setForm({ ...form, google_client_id: e.target.value })
                }
                placeholder="xxxxxx.apps.googleusercontent.com"
                className="mt-1"
                data-testid="google-client-id-input"
              />
            </div>
            <div>
              <Label>Google OAuth Client Secret</Label>
              <Input
                type="password"
                value={form.google_client_secret}
                onChange={(e) =>
                  setForm({ ...form, google_client_secret: e.target.value })
                }
                placeholder="GOCSPX-..."
                className="mt-1"
                data-testid="google-client-secret-input"
              />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button
              onClick={submit}
              disabled={saving}
              className="bg-midnight hover:bg-ink text-white"
              data-testid="settings-save"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salvar credenciais
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Modal de Diagnóstico (Fase 9.1) */}
      <Dialog open={showDiagnosis} onOpenChange={setShowDiagnosis}>
        <DialogContent
          className="max-w-2xl max-h-[85vh] overflow-y-auto"
          data-testid="diagnose-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Stethoscope className="w-5 h-5 text-emerald-700" />
              Diagnóstico do Auto-Recovery
            </DialogTitle>
            <DialogDescription>
              Verificação completa da capacidade de recuperar gravações,
              transcrições e notas Gemini automaticamente.
            </DialogDescription>
          </DialogHeader>

          {diagnosing && !diagnosis && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-700" />
              <span className="ml-2 text-sm text-slate-600">
                Verificando integração Google...
              </span>
            </div>
          )}

          {diagnosis && (
            <div className="space-y-4 mt-2" data-testid="diagnose-results">
              {/* Summary banner */}
              <div
                className={`p-3 rounded-md border text-sm ${
                  diagnosis.meet_api?.ok || diagnosis.drive_api?.ok
                    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                    : "bg-red-50 border-red-200 text-red-900"
                }`}
              >
                {diagnosis.summary}
              </div>

              {/* Conta conectada */}
              <section className="border border-slate-200 rounded-md p-3">
                <h4 className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                  Conta Google
                </h4>
                <dl className="grid grid-cols-[140px_1fr] gap-x-3 gap-y-1 text-sm">
                  <dt className="text-slate-600">Email:</dt>
                  <dd className="text-slate-900 font-medium" data-testid="diagnose-email">
                    {diagnosis.connected_email || "—"}
                  </dd>
                  <dt className="text-slate-600">Tipo de conta:</dt>
                  <dd>
                    {diagnosis.account_type === "workspace" && (
                      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                        Google Workspace ✓
                      </Badge>
                    )}
                    {diagnosis.account_type === "personal_gmail" && (
                      <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                        Gmail pessoal
                      </Badge>
                    )}
                    {diagnosis.account_type === "unknown" && (
                      <Badge variant="outline" className="border-slate-300">
                        Desconhecido
                      </Badge>
                    )}
                  </dd>
                  <dt className="text-slate-600">Escopos OAuth:</dt>
                  <dd>
                    {diagnosis.scopes_ok ? (
                      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                        Completos ✓
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                        Faltando {diagnosis.missing_scopes?.length || 0}
                      </Badge>
                    )}
                  </dd>
                </dl>
                {diagnosis.missing_scopes &&
                  diagnosis.missing_scopes.length > 0 && (
                    <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
                      <strong>Escopos faltando:</strong>
                      <ul className="ml-4 list-disc mt-1">
                        {diagnosis.missing_scopes.map((s) => (
                          <li key={s}>{s.split("/").pop()}</li>
                        ))}
                      </ul>
                      <p className="mt-1 italic">
                        Desconecte e reconecte o Google para aceitar as novas
                        permissões.
                      </p>
                    </div>
                  )}
              </section>

              {/* Meet REST API test */}
              <section className="border border-slate-200 rounded-md p-3">
                <h4 className="text-xs uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-2">
                  Meet REST API (Caminho A — Workspace)
                  {diagnosis.meet_api?.ok ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-600" />
                  )}
                </h4>
                {diagnosis.meet_api?.ok ? (
                  <p className="text-sm text-slate-700">
                    Funcional. {diagnosis.meet_api.sample_count} registro(s)
                    visível(is) na conta.
                  </p>
                ) : (
                  <p className="text-sm text-slate-700">
                    Indisponível.
                    {diagnosis.meet_api?.error && (
                      <span className="block text-xs text-slate-500 mt-1">
                        {diagnosis.meet_api.error}
                      </span>
                    )}
                  </p>
                )}
              </section>

              {/* Drive API test */}
              <section className="border border-slate-200 rounded-md p-3">
                <h4 className="text-xs uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-2">
                  Drive API (Caminho B — qualquer conta)
                  {diagnosis.drive_api?.ok ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-600" />
                  )}
                </h4>
                {diagnosis.drive_api?.ok ? (
                  <div>
                    <p className="text-sm text-slate-700">
                      Funcional.{" "}
                      {diagnosis.drive_api.recent_recordings_count} gravação(ões)
                      recente(s) na pasta &quot;Meet Recordings&quot;.
                    </p>
                    {diagnosis.drive_api.recent_files &&
                      diagnosis.drive_api.recent_files.length > 0 && (
                        <ul
                          className="mt-2 space-y-1 text-xs"
                          data-testid="diagnose-drive-files"
                        >
                          {diagnosis.drive_api.recent_files.slice(0, 5).map(
                            (f) => (
                              <li
                                key={f.id}
                                className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded px-2 py-1"
                              >
                                <span className="text-slate-700 truncate flex-1">
                                  {f.name}
                                </span>
                                {f.webViewLink && (
                                  <a
                                    href={f.webViewLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-700 underline shrink-0 text-[10px]"
                                  >
                                    abrir
                                  </a>
                                )}
                              </li>
                            )
                          )}
                        </ul>
                      )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-700">
                    {diagnosis.drive_api?.error ||
                      "Drive API indisponível."}
                  </p>
                )}
              </section>

              {/* Recommendations */}
              {diagnosis.recommendations &&
                diagnosis.recommendations.length > 0 && (
                  <section
                    className="border border-amber-200 bg-amber-50/40 rounded-md p-3"
                    data-testid="diagnose-recommendations"
                  >
                    <h4 className="text-xs uppercase tracking-wide text-amber-700 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Recomendações
                    </h4>
                    <ul className="space-y-2 text-sm text-slate-800">
                      {diagnosis.recommendations.map((r, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-amber-700 font-bold flex-shrink-0">•</span>
                          <span>{r}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
